import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useIdentities, useImportCsv, useInviteUser, useLinkUser, useManagers, useMapIdentity, useMe, useOrgMe, usePeople, useReports, useUsers, type ImportReport, type Person, type User, type VendorIdentity } from "../api/hooks";
import { Button, Chip, Empty, ErrorBox, Field, Loading, Page, Table, Tabs, statusTone } from "../components/ui";
import { fmtDate, title } from "../lib/format";

export const Route = createFileRoute("/organisation")({ component: Organisation });

type Tab = "people" | "import" | "signins" | "accounts";

function Organisation() {
  const managers = useManagers();
  const me = useOrgMe();
  const who = useMe();
  const unmapped = useIdentities(true);
  const isAdmin = who.data?.roles.some((r) => r === "hr_admin" || r === "platform_admin") ?? false;
  const [tab, setTab] = useState<Tab>("people");
  const [pick, setPick] = useState<string | null>(null);
  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: "people", label: "People" },
    { key: "import", label: "Import" },
    { key: "signins", label: "Sign-ins" },
    { key: "accounts", label: "Say who is who", count: unmapped.data?.length ?? 0 },
  ];
  return (
    <Page title="Organisation" sub="The reporting lines the product reasons over. People come from the HRIS connection or a CSV; names are shown only along your line.">
      {isAdmin && <Tabs tabs={tabs} active={tab} onChange={setTab} />}
      {tab === "people" && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="card kpi"><h4>Managers</h4><div className="v">{managers.data?.length ?? "—"}</div></div>
            <div className="card kpi"><h4>In my line</h4><div className="v">{me.data?.line_size ?? "—"}</div></div>
            <div className="card kpi"><h4>Direct reports</h4><div className="v">{me.data?.direct_reports.length ?? "—"}</div></div>
          </div>
          {managers.isPending ? (
            <Loading />
          ) : managers.isError ? (
            <ErrorBox error={managers.error} />
          ) : managers.data.length === 0 ? (
            <Empty
              title="No people yet"
              body="Connect Deel (or another HRIS) and run a sync, or import a CSV; the org graph is built from manager links."
              action={isAdmin ? <Button variant="light" onClick={() => setTab("import")}>Import a CSV →</Button> : undefined}
            />
          ) : (
            <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <PeopleTable title="Managers" rows={managers.data} onPick={setPick} picked={pick} />
              {pick ? <Reports managerKey={pick} managerName={managers.data.find((p) => p.person_key === pick)?.full_name ?? pick} /> : <Empty title="Pick a manager" body="Their direct reports show here." />}
            </div>
          )}
        </>
      )}
      {isAdmin && tab === "import" && <CsvImport />}
      {isAdmin && tab === "signins" && (
        <>
          <SignIns />
          <InviteUser managers={managers.data ?? []} />
          <LinkUser managers={managers.data ?? []} />
        </>
      )}
      {isAdmin && tab === "accounts" && <UnmappedIdentities />}
    </Page>
  );
}

/** MVP-B8. Most vendor accounts resolve themselves by e-mail. GitHub org members never
 *  carry one, and Slack, Jira and Linear can withhold it too — those land here, busiest
 *  first, because an unmapped account means its pull requests and tickets belong to
 *  nobody and drop out of every per-person view. */
function UnmappedIdentities() {
  const identities = useIdentities(true);
  const people = usePeople().data ?? [];
  const map = useMapIdentity();
  const [error, setError] = useState<unknown>(null);
  const rows = identities.data ?? [];
  if (identities.isPending) return <Loading />;
  if (rows.length === 0) return <Empty title="Everyone is placed" body="Every account a connected tool has reported is matched to a person. New ones land here when a tool withholds the e-mail." />;
  const waiting = rows.reduce((n, r) => n + r.work_items, 0);
  return (
    <div className="card mb-6">
      <h3 className="font-bold text-[15px]">Accounts we cannot place</h3>
      <p className="text-[13px] text-muted mt-1 max-w-[70ch]">
        These come from a connected tool that did not tell us the person's e-mail, so we cannot match them
        automatically. Until one is mapped, {waiting > 0 ? `the ${waiting} item${waiting === 1 ? "" : "s"} it owns belong` : "anything it owns belongs"} to
        nobody and will not appear in Work, Performance or 1:1 prep.
      </p>
      {error != null && <div className="mt-3"><ErrorBox error={error} /></div>}
      <div className="mt-3">
        <Table<VendorIdentity>
          rows={rows}
          keyOf={(r) => `${r.connector}:${r.external_id}`}
          cols={[
            { h: "Account", cell: (r) => (<div><div className="font-bold">{r.display ?? r.external_id}</div><div className="text-muted text-[12px]">{r.external_id}</div></div>) },
            { h: "Source", cell: (r) => <Chip>{title(r.connector)}</Chip>, w: "14%" },
            { h: "Waiting", cell: (r) => (r.work_items > 0 ? `${r.work_items} items` : "—"), w: "12%" },
            {
              h: "Person",
              w: "30%",
              cell: (r) => (
                <select
                  className="input"
                  defaultValue=""
                  disabled={map.isPending}
                  onChange={(e) => {
                    const person_key = e.target.value;
                    if (!person_key) return;
                    setError(null);
                    map.mutate({ connector: r.connector, external_id: r.external_id, person_key }, { onError: setError });
                  }}
                >
                  <option value="">Choose a person…</option>
                  {people.map((p) => (<option key={p.person_key} value={p.person_key}>{p.full_name ?? p.person_key}</option>))}
                </select>
              ),
            },
          ]}
        />
      </div>
      {people.length === 0 && (
        <p className="text-[13px] text-muted mt-3">Import people first — there is nobody to map these to yet.</p>
      )}
    </div>
  );
}

const CSV_TEMPLATE = "email,full_name,title,manager_email,department,team,location,timezone,status,start_date,hris_id\n";

function CsvImport() {
  const imp = useImportCsv();
  const [report, setReport] = useState<ImportReport | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [name, setName] = useState<string | null>(null);
  return (
    <div className="card mb-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-bold text-[15px]">Import people from a CSV</h3>
          <p className="text-[13px] text-muted mt-1 max-w-[70ch]">
            The HRIS fallback. One row per person; <code>email</code> is required, <code>manager_email</code> builds the reporting lines, <code>team</code> and <code>department</code> create the groups. Re-importing updates people by <code>hris_id</code> or email — nothing is deleted.
          </p>
          <p className="text-[12px] text-muted mt-1">Columns: <code>{CSV_TEMPLATE.trim()}</code></p>
        </div>
        <label className="btn btn-dark shrink-0 cursor-pointer">
          {imp.isPending ? "Importing…" : "Choose CSV"}
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            disabled={imp.isPending}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setName(f.name);
              setError(null);
              try {
                setReport(await imp.mutateAsync(await f.text()));
              } catch (err) {
                setError(err);
              } finally {
                e.target.value = "";
              }
            }}
          />
        </label>
      </div>
      {error != null && <div className="mt-3"><ErrorBox error={error} /></div>}
      {report && (
        <div className="notice mt-3">
          <b>{name}</b>: {report.people} people, {report.teams} teams, {report.departments} departments imported.
          {report.errors.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-[12px]">
              {report.errors.slice(0, 10).map((e) => <li key={e}>{e}</li>)}
              {report.errors.length > 10 && <li>…and {report.errors.length - 10} more</li>}
            </ul>
          )}
        </div>
      )}
      <Button variant="light" className="mt-3 !py-1.5 !px-3 !text-[12px]" onClick={() => void navigator.clipboard.writeText(CSV_TEMPLATE)}>Copy header row</Button>
    </div>
  );
}

const ROLE_LABEL: Record<string, string> = { platform_admin: "Humindly admin", hr_admin: "HR admin", viewer: "Manager / viewer" };

/** Who can sign in today, and which person each login acts as. */
function SignIns() {
  const users = useUsers();
  if (users.isPending) return <Loading />;
  if (users.isError) return <ErrorBox error={users.error} />;
  return (
    <div className="mb-6">
      <h3 className="font-bold text-[15px] mb-2">Sign-ins</h3>
      {users.data.length === 0 ? (
        <Empty title="No sign-ins yet" body="Invite one below." />
      ) : (
        <Table<User>
          rows={users.data}
          keyOf={(u) => u.cognito_sub}
          cols={[
            { h: "E-mail", cell: (u) => <span className="font-semibold">{u.email}</span> },
            { h: "Role", cell: (u) => <span className="text-muted">{ROLE_LABEL[u.role] ?? title(u.role)}</span>, w: "160px" },
            { h: "Acts as", cell: (u) => <span className="text-muted">{u.person_name ?? (u.person_key ? u.person_key : "—")}</span>, w: "200px" },
            { h: "Added", cell: (u) => <span className="text-muted">{fmtDate(u.created_at)}</span>, w: "120px" },
          ]}
        />
      )}
    </div>
  );
}

function InviteUser({ managers }: { managers: Person[] }) {
  const invite = useInviteUser();
  const [f, setF] = useState({ email: "", role: "viewer", person_key: "" });
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  return (
    <form
      className="card mb-6"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        invite.mutate({ ...f, person_key: f.person_key || null }, {
          onError: setError,
          onSuccess: (r) => { setDone(r.created ? `${r.email} invited — they will get an e-mail with a temporary password.` : `${r.email} already had a sign-in; it is linked now.`); setF({ email: "", role: "viewer", person_key: "" }); },
        });
      }}
    >
      <h3 className="font-bold text-[15px]">Invite a sign-in</h3>
      <p className="text-[13px] text-muted mt-1 max-w-[70ch]">Creates their login for this organisation and e-mails a temporary password. Pick the person record for managers so Team Health knows whose team is theirs; HR admins do not need one.</p>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <Field label="Work e-mail"><input className="input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Role"><select className="input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}><option value="viewer">Manager / viewer</option><option value="hr_admin">HR admin</option></select></Field>
        <Field label="Person">
          <select className="input" value={f.person_key} onChange={(e) => setF({ ...f, person_key: e.target.value })}>
            <option value="">— none —</option>
            {managers.map((m) => <option key={m.person_key} value={m.person_key}>{m.full_name ?? m.person_key}</option>)}
          </select>
        </Field>
      </div>
      {error != null && <div className="mt-3"><ErrorBox error={error} /></div>}
      {done && <div className="notice mt-3">{done}</div>}
      <Button type="submit" className="mt-3" disabled={invite.isPending}>{invite.isPending ? "Inviting…" : "Send invite"}</Button>
    </form>
  );
}

function LinkUser({ managers }: { managers: Person[] }) {
  const link = useLinkUser();
  const who = useMe();
  const people = usePeople();
  const org = useOrgMe();
  const [f, setF] = useState({ cognito_sub: "", email: "", role: "viewer", person_key: "" });
  // Linking yourself must not demote you: carry the highest role the token already has.
  const myRole = who.data?.roles.includes("hr_admin") || who.data?.roles.includes("platform_admin") ? "hr_admin" : "viewer";
  const mine = () => {
    const email = who.data?.email ?? "";
    const guess = (people.data ?? []).find((p) => (p.work_email ?? "").toLowerCase() === email.toLowerCase());
    setF({ cognito_sub: who.data?.sub ?? "", email, role: myRole, person_key: guess?.person_key ?? "" });
  };
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  return (
    <form
      className="card mb-6"
      onSubmit={(e) => {
        e.preventDefault();
        link.mutate({ ...f, person_key: f.person_key || null }, { onError: setError, onSuccess: () => { setDone(`${f.email} linked`); setF({ cognito_sub: "", email: "", role: "viewer", person_key: "" }); } });
      }}
    >
      <h3 className="font-bold text-[15px]">Link an existing sign-in to a person</h3>
      <p className="text-[13px] text-muted mt-1 max-w-[70ch]">A manager sees Team Health once their Cognito user (its <code>sub</code>) is linked to their person record. Admins do not need a person unless they manage people too.</p>
      {who.data && !org.data?.me && (
        <Button type="button" variant="light" className="mt-3" onClick={mine}>Use my sign-in →</Button>
      )}
      <div className="grid grid-cols-4 gap-3 mt-3">
        <Field label="Cognito sub"><input className="input" required value={f.cognito_sub} onChange={(e) => setF({ ...f, cognito_sub: e.target.value })} /></Field>
        <Field label="Email"><input className="input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Role"><select className="input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}><option value="viewer">Manager / viewer</option><option value="hr_admin">HR admin</option></select></Field>
        <Field label="Person">
          <select className="input" value={f.person_key} onChange={(e) => setF({ ...f, person_key: e.target.value })}>
            <option value="">— none —</option>
            {(people.data ?? managers).map((m) => <option key={m.person_key} value={m.person_key}>{m.full_name ?? m.person_key}</option>)}
          </select>
        </Field>
      </div>
      {error != null && <div className="mt-3"><ErrorBox error={error} /></div>}
      {done && <div className="notice mt-3">{done}</div>}
      <Button type="submit" className="mt-3" disabled={link.isPending}>Link</Button>
    </form>
  );
}

function PeopleTable({ title: t, rows, onPick, picked }: { title: string; rows: Person[]; onPick?: (k: string) => void; picked?: string | null }) {
  return (
    <div>
      <h3 className="text-[15px] font-bold mb-2">{t}</h3>
      <Table
        rows={rows}
        keyOf={(p) => p.person_key}
        cols={[
          { h: "Person", cell: (p) => <button className={`text-left ${picked === p.person_key ? "text-link" : ""}`} onClick={() => onPick?.(p.person_key)}><span className="font-semibold">{p.full_name ?? p.person_key}</span><span className="sub">{p.title ?? ""}{p.department ? ` · ${p.department}` : ""}</span></button> },
          { h: "Team", cell: (p) => <span className="text-muted">{p.team_key ?? "—"}</span>, w: "120px" },
          { h: "Location", cell: (p) => <span className="text-muted">{p.location ?? "—"}</span>, w: "110px" },
          { h: "Status", cell: (p) => <Chip tone={statusTone(p.status)}>{title(p.status)}</Chip>, w: "110px" },
        ]}
      />
    </div>
  );
}

function Reports({ managerKey, managerName }: { managerKey: string; managerName: string }) {
  const reports = useReports(managerKey);
  if (reports.isPending) return <Loading />;
  if (reports.isError) return <ErrorBox error={reports.error} />;
  return <PeopleTable title={`Reports to ${managerName}`} rows={reports.data} />;
}
